import { useEffect, useMemo, useState, Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchCasesForUnits,
  fetchFzCasesForUnits,
  updateDeliveryUnits,
} from "../../store/superAdmin/unitsSlice";
import { fetchSearchCaseUnitsByCaseId } from "../../store/common/searchSlice";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import EmployeeNameEditor, {
  WrappingSelect,
} from "../../components/admin/EmployeeNameEditor";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
} from "../../components/admin/AdminPageShell";
import {
  InternalRedesignsUnitsPanel,
  JobCardNotesModal,
  UnitsBillingNoteModal,
  LiveCaseUnitsHint,
  OriginalValue,
  RedesignScopeBadges,
  RedesignScopeEditor,
  applyClientRedesignScopeChange,
  categoryAccent,
  categoryRowClass,
  defaultInternalAssignments,
  parseUnitsRedesignLabels,
  serializeInternalAssignments,
  formatPeopleUnits,
  qcCreditForPerson,
  qcUnitsFromAssignedWork,
  sameText,
  splitNames,
  sumUnits,
  unitsTableInputClass,
  unitsTableTd,
  unitsTableTh,
  type InternalRedesignAssignment,
  type UnitsBillingNoteModalState,
  type UnitsNotesModalState,
  type UnitsRedesignScope,
} from "../../components/admin/unitsTableShared";
import ToothNumberPicker from "../../components/admin/ToothNumberPicker";
import UnitsModelEditor from "../../components/admin/UnitsModelEditor";
import UnitsExtractPanel from "../../components/admin/UnitsExtractPanel";
import {
  BILLING_CATEGORIES,
  UNITS_CRM_ROLES,
  isUnitsQc2Role,
  formatModelTypes,
  hasModelCategorySelection,
  hasModelWorkSelection,
  modelCategoryWorkUnits,
  modelDesignerUnits,
  modelOtherItemWorkUnits,
  parseDesignerWork,
  parseModelTypes,
  persistModelTypes,
  suggestBillingCategory,
} from "../../utils/jobCard";

const LIMIT = 25;

type CaseUnitsProps = {
  /** Dedicated FZ lab units queue — blank form except CRM completer name. */
  variant?: "default" | "fz";
};

const CaseUnits = ({ variant = "default" }: CaseUnitsProps) => {
  const isFz = variant === "fz";
  const dispatch = useAppDispatch();
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { user } = useAppSelector((state) => state.auth);
  const { allEmployees } = useAppSelector((state) => state.employee);
  const getRowKey = (row: any) => `${row.case_id}-${row.details}`;

  const {
    cases,
    totalCount,
    currentPage,
    totalPages,
    loading: unitsLoading,
  } = useAppSelector((state) => state.deliveryUnits);

  const {
    searchedCaseUnits,
    loading: { searchedCaseUnits: searchLoading },
  } = useAppSelector((state) => state.search);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const isSearching = useMemo(() => !!search.trim(), [search]);

  // FZ page always uses the FZ endpoint (including search); default uses global search.
  const activeCases = !isFz && isSearching ? searchedCaseUnits.data : cases;
  const activeTotalCount =
    !isFz && isSearching ? searchedCaseUnits.totalCount : totalCount;
  const activeCurrentPage =
    !isFz && isSearching ? searchedCaseUnits.currentPage : currentPage;
  const activeTotalPages =
    !isFz && isSearching ? searchedCaseUnits.totalPages : totalPages;
  const loading =
    !isFz && isSearching ? searchLoading : unitsLoading;

  const [editedDeliveryUnits, setEditedDeliveryUnits] = useState<
    Record<string, number>
  >({});
  const [editedDesigners, setEditedDesigners] = useState<Record<string, string>>(
    {},
  );
  const [editedDesignerUnits, setEditedDesignerUnits] = useState<
    Record<string, Record<string, number>>
  >({});
  const [editedDesignerWork, setEditedDesignerWork] = useState<
    Record<string, Record<string, "normal" | "model">>
  >({});
  const [editedQc2, setEditedQc2] = useState<Record<string, string>>({});
  const [editedDesignTn, setEditedDesignTn] = useState<Record<string, string>>(
    {},
  );
  const [editedDeliveryTn, setEditedDeliveryTn] = useState<
    Record<string, string>
  >({});
  const [editedCategory, setEditedCategory] = useState<Record<string, string>>(
    {},
  );
  const [categoryEdited, setCategoryEdited] = useState<Record<string, boolean>>(
    {},
  );
  const [editedFilesBy, setEditedFilesBy] = useState<Record<string, string>>(
    {},
  );
  const [editedCrm, setEditedCrm] = useState<Record<string, string>>({});
  const [editedModelType, setEditedModelType] = useState<
    Record<string, string[]>
  >({});
  const [editedModelBy, setEditedModelBy] = useState<Record<string, string>>(
    {},
  );
  const [editedModelUnits, setEditedModelUnits] = useState<
    Record<string, number>
  >({});
  const [editedUnitsNotes, setEditedUnitsNotes] = useState<
    Record<string, string>
  >({});
  const [editedIrAssignments, setEditedIrAssignments] = useState<
    Record<string, InternalRedesignAssignment[]>
  >({});
  const [editedRedesignCategory, setEditedRedesignCategory] = useState<
    Record<string, string>
  >({});
  const [assigningKey, setAssigningKey] = useState<string | null>(null);
  const [notesModal, setNotesModal] = useState<UnitsNotesModalState | null>(
    null,
  );
  const [billingNoteModal, setBillingNoteModal] =
    useState<UnitsBillingNoteModalState | null>(null);

  const designersList = useMemo(
    () =>
      allEmployees?.filter(
        (emp) =>
          emp.verified &&
          ["Designer", "Senior Designer", "Captain", "Assistant Captain", "Super Admin"].includes(
            emp.role,
          ),
      ) ?? [],
    [allEmployees],
  );

  const qc2List = useMemo(
    () =>
      allEmployees?.filter(
        (emp) => emp.verified && isUnitsQc2Role(emp.role, isFz),
      ) ?? [],
    [allEmployees, isFz],
  );

  const crmList = useMemo(
    () =>
      allEmployees?.filter(
        (emp) =>
          emp.verified &&
          UNITS_CRM_ROLES.includes(
            emp.role as (typeof UNITS_CRM_ROLES)[number],
          ),
      ) ?? [],
    [allEmployees],
  );

  const resetEdits = () => {
    setEditedDeliveryUnits({});
    setEditedDesigners({});
    setEditedDesignerUnits({});
    setEditedDesignerWork({});
    setEditedQc2({});
    setEditedDesignTn({});
    setEditedDeliveryTn({});
    setEditedCategory({});
    setCategoryEdited({});
    setEditedFilesBy({});
    setEditedCrm({});
    setEditedModelType({});
    setEditedModelBy({});
    setEditedModelUnits({});
    setEditedUnitsNotes({});
    setEditedIrAssignments({});
    setEditedRedesignCategory({});
  };

  const clearRowEdits = (rowKey: string) => {
    const clearKey = (
      setter: (updater: (prev: Record<string, any>) => Record<string, any>) => void,
    ) => {
      setter((prev) => {
        if (!(rowKey in prev)) return prev;
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
    };
    clearKey(setEditedDeliveryUnits);
    clearKey(setEditedDesigners);
    clearKey(setEditedDesignerUnits);
    clearKey(setEditedDesignerWork);
    clearKey(setEditedQc2);
    clearKey(setEditedDesignTn);
    clearKey(setEditedDeliveryTn);
    clearKey(setEditedCategory);
    clearKey(setCategoryEdited);
    clearKey(setEditedFilesBy);
    clearKey(setEditedCrm);
    clearKey(setEditedModelType);
    clearKey(setEditedModelBy);
    clearKey(setEditedModelUnits);
    clearKey(setEditedUnitsNotes);
    clearKey(setEditedIrAssignments);
    clearKey(setEditedRedesignCategory);
  };

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isFz) {
      dispatch(
        fetchFzCasesForUnits({
          page,
          limit: LIMIT,
          search: search.trim(),
        }),
      );
    } else if (isSearching) {
      dispatch(
        fetchSearchCaseUnitsByCaseId({
          caseId: search,
          page,
          limit: LIMIT,
        }),
      );
    } else {
      dispatch(
        fetchCasesForUnits({
          page,
          limit: LIMIT,
        }),
      );
    }

    resetEdits();
  }, [dispatch, page, search, isSearching, isFz]);

  const handleAssign = async (row: any) => {
    const key = getRowKey(row);
    try {
      setAssigningKey(key);
      const designer = editedDesigners[key] ?? row.designers;
      const designerNames = splitNames(designer);
      const designerUnits =
        editedDesignerUnits[key] ?? row.designer_units ?? {};
      const modelTypes = persistModelTypes(
        hasModelCategorySelection(
          editedModelType[key] ?? row.model_types ?? row.model_type,
        ),
        editedModelType[key] ?? row.model_types ?? row.model_type,
      );
      const hasModel = hasModelCategorySelection(modelTypes);
      const defaultModelUnits = modelDesignerUnits(hasModel, modelTypes);
      const hasModelWork = hasModel || hasModelWorkSelection(modelTypes);
      const designerWork = parseDesignerWork(
        editedDesignerWork[key] ?? row.designer_work,
        designerNames,
        editedModelBy[key] ?? row.model_by,
        hasModelWork,
      );
      const extraCount = designerNames.slice(1).length;
      const primaryAuto = extraCount
        ? modelCategoryWorkUnits(modelTypes)
        : defaultModelUnits;
      const modelBy = hasModelWork
        ? editedModelBy[key] ||
          row.model_by ||
          designerNames[0] ||
          ""
        : "";

      await dispatch(
        updateDeliveryUnits({
          units_By:
            user && user?.role === "Super Admin"
              ? user.user_name
              : employee
                ? employee.emp_name
                : "System",
          cases: [
            {
              caseId: row.case_id,
              caseType: row.type,
              newDeliveryUnits:
                editedDeliveryUnits[key] ?? Number(row.delivery_units || 0),
              designUnit: sumUnits(
                Object.fromEntries(
                  Object.entries(designerUnits).filter(
                    ([name]) => designerWork[name] !== "model",
                  ),
                ),
              ),
              patientName:
                row.service_type === "filledOrder"
                  ? row.details || row.patient_name || null
                  : null,
              designer,
              captain: editedQc2[key] ?? row.qc2_users,
              designToothNumbers:
                editedDesignTn[key] ?? row.design_tooth_numbers ?? "",
              deliveryToothNumbers:
                editedDeliveryTn[key] ?? row.delivery_tooth_numbers ?? "",
              filesBy: editedFilesBy[key] ?? row.files_by ?? "",
              filesUnits: Number(row.files_units) || 0,
              model: hasModel ? "Yes" : "No",
              modelType: modelTypes.join(", "),
              modelTypes,
              modelBy,
              modelUnits: hasModelWork
                ? editedModelUnits[key] ?? primaryAuto
                : 0,
              designerUnits,
              designerWork,
              category: editedCategory[key] ?? row.category ?? "",
              crm: editedCrm[key] ?? row.crm ?? "",
              unitsNotes: editedUnitsNotes[key] ?? row.units_notes ?? "",
              ...(row.type === "redesign"
                ? {
                    redesignCategory:
                      editedRedesignCategory[key] ??
                      row.redesign_category ??
                      "",
                  }
                : {}),
              internalRedesigns: serializeInternalAssignments(
                editedIrAssignments[key] ??
                  defaultInternalAssignments(row.internal_redesigns),
              ),
            },
          ],
        }),
      ).unwrap();

      clearRowEdits(key);

      if (isFz) {
        await dispatch(
          fetchFzCasesForUnits({
            page,
            limit: LIMIT,
            search: search.trim(),
          }),
        );
      } else if (isSearching) {
        await dispatch(
          fetchSearchCaseUnitsByCaseId({
            caseId: search,
            page,
            limit: LIMIT,
          }),
        );
      } else {
        await dispatch(
          fetchCasesForUnits({
            page,
            limit: LIMIT,
          }),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setAssigningKey(null);
    }
  };

  const colCount = 12;

  const inputClass = unitsTableInputClass;
  const th = unitsTableTh;
  const td = unitsTableTd;

  const revertField = (
    setter: (updater: (prev: Record<string, any>) => Record<string, any>) => void,
    rowKey: string,
  ) => {
    setter((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  if (user?.role !== "Super Admin" && employee?.role === "CRM") {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{isFz ? "FZ Units" : "Case Units"}</title>
      </Helmet>

      <AdminPageShell className="flex h-full min-h-0 flex-col gap-3 overflow-hidden text-[1.05rem]">
        <AdminPageHeader
          title={isFz ? "FZ Units" : "Case Units"}
          subtitle={
            isFz
              ? "FZ lab cases after Ready-to-Upload completion. Only the CRM who marked the case complete is prefilled — enter designers, QC, category, and units manually."
              : "After upload: live cases, plus client redesigns once live units are marked. Redesign employee units default to Minor 1, Major half of live, New Design full live — all editable."
          }
        >
          <AdminSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={
              isFz ? "Search by Case ID or patient name…" : "Search by Case ID…"
            }
          />
          {!isFz ? <UnitsExtractPanel /> : null}
          {!isFz ? (
          <a
            href="/dashboard/admin/units/ledger"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition"
          >
            Units Ledger
          </a>
          ) : null}
          {!isFz ? (
          <a
            href="/dashboard/admin/units/recent"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition"
          >
            Last 50 assigned
          </a>
          ) : (
          <a
            href="/dashboard/admin/units"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition"
          >
            All Units
          </a>
          )}
          <div
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#2fa9bf] text-xs font-semibold shrink-0"
            title="Cases still waiting for units to be marked"
          >
            <span className="text-sm font-bold tabular-nums leading-none">
              {unitsLoading && totalCount === 0 ? "…" : totalCount}
            </span>
            {totalCount === 1 ? "case pending" : "cases pending"}
          </div>
          <AdminPagination
            tone="onAccent"
            page={activeCurrentPage}
            totalPages={activeTotalPages}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
            disabled={loading}
          />
        </AdminPageHeader>

        <AdminTableCard className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full table-fixed border-separate border-spacing-0">
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-[#E8E8E4]">
                  <th className={`${th} w-[5%]`}>Case</th>
                  <th className={`${th} w-[7%]`}>CRM</th>
                  <th className={`${th} w-[14%]`} title="Billing Category">
                    Bill. Cat.
                  </th>
                  <th className={`${th} w-[14%]`}>Designer</th>
                  <th className={`${th} w-[10%]`}>QC2</th>
                  <th className={`${th} w-[8%]`} title="Design tooth numbers">
                    Design TN
                  </th>
                  <th className={`${th} w-[8%]`} title="Delivery tooth numbers">
                    Delivery TN
                  </th>
                  <th className={`${th} w-[4%]`} title="Units">
                    Units
                  </th>
                  <th className={`${th} w-[9%]`} title="Files by">
                    Files
                  </th>
                  <th className={`${th} w-[10%]`}>Model</th>
                  <th className={`${th} w-[5%]`}>Notes</th>
                  <th className={`${th} w-[6%]`}>Assign</th>
                </tr>
              </thead>

              <tbody>
                {activeCases.map((row: any, index: number) => {
                  const key = getRowKey(row);
                  const currentDesignTn =
                    editedDesignTn[key] ?? row.design_tooth_numbers ?? "";
                  const currentDeliveryTn =
                    editedDeliveryTn[key] ?? row.delivery_tooth_numbers ?? "";
                  const origModelTypes = parseModelTypes(
                    row.model_types ?? row.model_type,
                  );
                  const currModelTypeRows = parseModelTypes(
                    editedModelType[key] ?? origModelTypes,
                  );
                  const hasModel = hasModelCategorySelection(currModelTypeRows);
                  const origModel = hasModelCategorySelection(origModelTypes)
                    ? "Yes"
                    : "No";
                  const defaultModelUnits = modelDesignerUnits(
                    hasModel,
                    currModelTypeRows,
                  );
                  const categoryModelUnits = modelCategoryWorkUnits(
                    currModelTypeRows,
                  );
                  const otherModelUnits = modelOtherItemWorkUnits(
                    currModelTypeRows,
                  );
                  const extraModelCount = splitNames(
                    editedDesigners[key] ?? row.designers,
                  ).slice(1).length;
                  const primaryAutoModelUnits = extraModelCount
                    ? categoryModelUnits
                    : defaultModelUnits;
                  const hasModelWork =
                    hasModel || hasModelWorkSelection(currModelTypeRows);
                  const currModelUnits = hasModelWork
                    ? editedModelUnits[key] ?? primaryAutoModelUnits
                    : 0;
                  const jobCardNotes = String(
                    row.notes || row.job_card?.notes || "",
                  ).trim();
                  const jobCardModelNotes = String(
                    row.model_notes || row.job_card?.model_notes || "",
                  ).trim();
                  const origUnitsNotes = String(
                    row.units_notes || row.job_card?.units_notes || "",
                  ).trim();
                  const currUnitsNotes = editedUnitsNotes[key] ?? origUnitsNotes;
                  const hasJobCardNotes = Boolean(
                    jobCardNotes || (hasModel && jobCardModelNotes),
                  );

                  const currentCategory =
                    editedCategory[key] ?? row.category ?? "";
                  const origCrm = row.crm ?? "";
                  const currCrm = editedCrm[key] ?? origCrm;
                  const origCategory = row.category ?? "";
                  const origDesigner = row.designers ?? "";
                  const currDesigner = editedDesigners[key] ?? origDesigner;
                  const origDesignerUnits = row.designer_units ?? {};
                  const currDesignerUnits =
                    editedDesignerUnits[key] ?? origDesignerUnits;
                  const origQc2 = splitNames(row.qc2_users)[0] || "";
                  const currQc2 =
                    splitNames(editedQc2[key] ?? row.qc2_users)[0] || "";
                  const origDesignTn = row.design_tooth_numbers ?? "";
                  const origDeliveryTn = row.delivery_tooth_numbers ?? "";
                  const origDeliveryUnits = Number(row.delivery_units || 0);
                  const currDeliveryUnits =
                    editedDeliveryUnits[key] ?? origDeliveryUnits;
                  const origFilesBy = row.files_by ?? "";
                  const currFilesBy = editedFilesBy[key] ?? origFilesBy;
                  const origModelBy =
                    origModel === "Yes" ||
                    hasModelWorkSelection(origModelTypes)
                      ? row.model_by || splitNames(row.designers)[0] || ""
                      : "";
                  const currModelBy = editedModelBy[key] ?? origModelBy;
                  const currDesignerWork = parseDesignerWork(
                    editedDesignerWork[key] ?? row.designer_work,
                    splitNames(currDesigner),
                    currModelBy,
                    hasModelWork,
                  );
                  const currQcUnits = qcUnitsFromAssignedWork({
                    designerNames: splitNames(currDesigner),
                    designerUnits: currDesignerUnits,
                    designerWork: currDesignerWork,
                    hasModel: hasModelWork,
                    modelUnits: currModelUnits,
                  });
                  const qcCredit = qcCreditForPerson(
                    currQc2,
                    splitNames(currDesigner),
                    currDesignerWork,
                    currQcUnits,
                  );
                  const irItems = row.internal_redesigns || [];
                  const irAssignments =
                    editedIrAssignments[key] ??
                    defaultInternalAssignments(irItems);
                  const origRedesignCategory = row.redesign_category ?? "";
                  const currRedesignCategory =
                    editedRedesignCategory[key] ?? origRedesignCategory;
                  const rowKey = `${row.type}-${row.case_id}-${row.details || ""}-${index}`;

                  return (
                    <Fragment key={rowKey}>
                    <tr
                      className={`border-b border-[#E3E8E8] last:border-0 transition-colors ${categoryRowClass(
                        currentCategory,
                        index,
                        false,
                      )} ${categoryAccent(currentCategory)}`}
                    >
                      <td className={`${td} break-words`}>
                        <a
                          href={(() => {
                            const isRedesign = /^RD-\d+-/.test(row.case_id);
                            const cleanCaseId = isRedesign
                              ? row.case_id.replace(/^RD-\d+-/, "")
                              : row.case_id;
                            return isRedesign
                              ? `/dashboard/admin/cases/${cleanCaseId}?tab=redesign`
                              : `/dashboard/admin/cases/${cleanCaseId}?tab=previews`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[1.2rem] font-semibold text-[#2fa9bf] hover:text-[#2296aa] hover:underline break-all leading-snug"
                        >
                          {row.case_id}
                        </a>
                        {row.type === "redesign" ? (
                          <>
                            <RedesignScopeEditor
                              labels={currRedesignCategory}
                              onScopeChange={(scope: UnitsRedesignScope) => {
                                const next = applyClientRedesignScopeChange({
                                  currentLabels: currRedesignCategory,
                                  nextScope: scope,
                                  liveDeliveryUnits: row.live_delivery_units,
                                  currentDeliveryUnits: currDeliveryUnits,
                                  designer: currDesigner,
                                  designerUnits: currDesignerUnits,
                                });
                                setEditedRedesignCategory((prev) => ({
                                  ...prev,
                                  [key]: next.nextLabels,
                                }));
                                if (next.nextDeliveryUnits != null) {
                                  setEditedDeliveryUnits((prev) => ({
                                    ...prev,
                                    [key]: next.nextDeliveryUnits as number,
                                  }));
                                }
                                if (next.nextDesignerUnits) {
                                  setEditedDesignerUnits((prev) => ({
                                    ...prev,
                                    [key]: next.nextDesignerUnits as Record<
                                      string,
                                      number
                                    >,
                                  }));
                                }
                              }}
                            />
                            <OriginalValue
                              original={
                                parseUnitsRedesignLabels(origRedesignCategory)
                                  .scope
                              }
                              current={
                                parseUnitsRedesignLabels(currRedesignCategory)
                                  .scope
                              }
                              onRestore={() =>
                                revertField(setEditedRedesignCategory, key)
                              }
                            />
                          </>
                        ) : (
                          <RedesignScopeBadges labels={row.redesign_category} />
                        )}
                        {row.type === "redesign" ? (
                          <LiveCaseUnitsHint
                            liveDeliveryUnits={row.live_delivery_units}
                            liveUnitsBy={row.live_units_By}
                          />
                        ) : null}
                        {irItems.length ? (
                          <div className="mt-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-900">
                            {irItems.length} internal redesign
                            {irItems.length === 1 ? "" : "s"}
                          </div>
                        ) : (
                          <div className="mt-1 text-[11px] text-[#8A8A85]">
                            No internal redesigns
                          </div>
                        )}
                        {row.service_type === "filledOrder" &&
                        (row.patient_name || row.details) ? (
                          <div className="mt-1 text-[13px] font-medium text-[#6B6B66] whitespace-normal">
                            {row.patient_name || row.details}
                          </div>
                        ) : null}
                      </td>

                      <td className={td}>
                        <div>
                          <EmployeeNameEditor
                            value={currCrm}
                            options={crmList}
                            allowMultiple={false}
                            allowEmpty
                            placeholder="CRM"
                            onChange={(value) => {
                              setEditedCrm((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                            }}
                          />
                        </div>
                        <OriginalValue
                          original={origCrm}
                          current={currCrm}
                          onRestore={() => revertField(setEditedCrm, key)}
                        />
                      </td>

                      <td className={`${td}`}>
                        <div>
                          <WrappingSelect
                            value={currentCategory}
                            onChange={(value) => {
                              setEditedCategory((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                              setCategoryEdited((prev) => ({
                                ...prev,
                                [key]: true,
                              }));
                            }}
                            placeholder="Select"
                            title={currentCategory}
                          >
                            <option value="">Select</option>
                            {BILLING_CATEGORIES.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </WrappingSelect>
                        </div>
                        <OriginalValue
                          original={origCategory}
                          current={currentCategory}
                          onRestore={() => {
                            revertField(setEditedCategory, key);
                            revertField(setCategoryEdited, key);
                          }}
                        />
                      </td>

                      <td className={`${td}`}>
                        <div>
                          <EmployeeNameEditor
                            value={currDesigner}
                            options={designersList}
                            placeholder="Designer"
                            showUnits
                            unitsByName={currDesignerUnits}
                            showModelPerson={hasModelWork}
                            modelBy={currModelBy}
                            modelUnits={currModelUnits}
                            autoModelUnits={primaryAutoModelUnits}
                            autoOtherModelUnits={otherModelUnits}
                            modelNotesHint={
                              [jobCardNotes, jobCardModelNotes]
                                .filter(Boolean)
                                .join(" ")
                            }
                            onModelByChange={(name) => {
                              setEditedModelBy((prev) => ({
                                ...prev,
                                [key]: name,
                              }));
                            }}
                            onModelUnitsChange={(units) => {
                              setEditedModelUnits((prev) => ({
                                ...prev,
                                [key]: Math.max(units, extraModelCount ? 0 : 1),
                              }));
                            }}
                            onWorkChange={(work) => {
                              setEditedDesignerWork((prev) => ({
                                ...prev,
                                [key]: work,
                              }));
                            }}
                            onChange={(value) => {
                              setEditedDesigners((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                            }}
                            onUnitsChange={(units) => {
                              setEditedDesignerUnits((prev) => ({
                                ...prev,
                                [key]: units,
                              }));
                            }}
                          />
                        </div>
                        <OriginalValue
                          original={formatPeopleUnits(
                            origDesigner,
                            origDesignerUnits,
                          )}
                          current={formatPeopleUnits(
                            currDesigner,
                            currDesignerUnits,
                          )}
                          onRestore={() => {
                            revertField(setEditedDesigners, key);
                            revertField(setEditedDesignerUnits, key);
                            revertField(setEditedDesignerWork, key);
                            revertField(setEditedModelBy, key);
                            revertField(setEditedModelUnits, key);
                          }}
                        />
                      </td>

                      <td className={td}>
                        <div>
                          <EmployeeNameEditor
                            value={currQc2}
                            options={qc2List}
                            allowMultiple={false}
                            placeholder="QC2"
                            onChange={(value) => {
                              setEditedQc2((prev) => ({ ...prev, [key]: value }));
                            }}
                          />
                        </div>
                        <div className="mt-1 text-[12px] font-medium text-[#4A4A48]">
                          {qcCredit.units} units
                          <span className="ml-1 font-normal text-[#8A8A85]">
                            {qcCredit.samePerson
                              ? "QC2 recorded, design units only"
                              : "(design + model)"}
                          </span>
                        </div>
                        <OriginalValue
                          original={origQc2}
                          current={currQc2}
                          onRestore={() => revertField(setEditedQc2, key)}
                        />
                      </td>

                      <td className={td}>
                        <ToothNumberPicker
                          compact
                          label="Tooth no. + Enter"
                          value={currentDesignTn}
                          onChange={(value) => {
                            setEditedDesignTn((prev) => ({
                              ...prev,
                              [key]: value,
                            }));
                          }}
                        />
                        <OriginalValue
                          original={origDesignTn}
                          current={currentDesignTn}
                          onRestore={() => revertField(setEditedDesignTn, key)}
                        />
                      </td>

                      <td className={td}>
                        <ToothNumberPicker
                          compact
                          label="Tooth no. + Enter"
                          value={currentDeliveryTn}
                          onChange={(value) => {
                            setEditedDeliveryTn((prev) => ({
                              ...prev,
                              [key]: value,
                            }));
                          }}
                        />
                        <OriginalValue
                          original={origDeliveryTn}
                          current={currentDeliveryTn}
                          onRestore={() => revertField(setEditedDeliveryTn, key)}
                        />
                      </td>

                      <td className={td}>
                        <input
                          type="number"
                          min={0}
                          value={currDeliveryUnits}
                          onChange={(e) => {
                            const nextUnits = Number(e.target.value);
                            const prevUnits = currDeliveryUnits;
                            const prevSuggested = suggestBillingCategory({
                              serviceType: row.service_type || row.treatments,
                              units: Number(prevUnits) || 0,
                              model: hasModel,
                            });
                            setEditedDeliveryUnits((prev) => ({
                              ...prev,
                              [key]: nextUnits,
                            }));
                            if (row.type === "redesign") {
                              const firstDesigner = splitNames(currDesigner)[0];
                              if (
                                firstDesigner &&
                                Number(currDesignerUnits[firstDesigner] || 0) ===
                                  Number(prevUnits)
                              ) {
                                setEditedDesignerUnits((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...currDesignerUnits,
                                    [firstDesigner]: nextUnits,
                                  },
                                }));
                              }
                            }
                            if (
                              !categoryEdited[key] &&
                              (!currentCategory ||
                                currentCategory === prevSuggested)
                            ) {
                              setEditedCategory((prev) => ({
                                ...prev,
                                [key]: suggestBillingCategory({
                                  serviceType:
                                    row.service_type || row.treatments,
                                  units: nextUnits,
                                  model: hasModel,
                                }),
                              }));
                            }
                          }}
                          className={inputClass}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setBillingNoteModal({
                              caseId: row.case_id,
                              rowKey: key,
                              unitsNotes: currUnitsNotes,
                            })
                          }
                          className={`mt-1.5 block w-full rounded-md px-2 py-1 text-left text-[12px] font-medium ${
                            currUnitsNotes
                              ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
                              : "border border-dashed border-[#C9D4D6] bg-white text-[#8A8A85] hover:border-[#2fa9bf] hover:text-[#2fa9bf]"
                          }`}
                          title={
                            currUnitsNotes
                              ? currUnitsNotes
                              : "Billing note (Generate Bill only)"
                          }
                        >
                          {currUnitsNotes ? "Edit note" : "Add note"}
                        </button>
                        <OriginalValue
                          original={origDeliveryUnits}
                          current={currDeliveryUnits}
                          onRestore={() =>
                            revertField(setEditedDeliveryUnits, key)
                          }
                        />
                      </td>

                      <td className={td}>
                        <div>
                          <EmployeeNameEditor
                            value={currFilesBy}
                            options={designersList}
                            allowMultiple={false}
                            allowEmpty
                            placeholder="Files by"
                            onChange={(value) => {
                              setEditedFilesBy((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                            }}
                          />
                        </div>
                        <OriginalValue
                          original={origFilesBy}
                          current={currFilesBy}
                          onRestore={() => revertField(setEditedFilesBy, key)}
                        />
                      </td>

                      <td className={td}>
                        <div>
                          <UnitsModelEditor
                            value={{
                              model: hasModel ? "Yes" : "No",
                              modelTypes: currModelTypeRows,
                              modelBy: currModelBy,
                            }}
                            onChange={(next) => {
                              const nextHasWork =
                                next.model === "Yes" ||
                                hasModelWorkSelection(next.modelTypes);
                              setEditedModelType((prev) => ({
                                ...prev,
                                [key]: next.modelTypes,
                              }));
                              setEditedModelUnits((prev) => {
                                const current = prev[key];
                                if (
                                  current != null &&
                                  current !== primaryAutoModelUnits
                                ) {
                                  if (nextHasWork) return prev;
                                  const nextUnits = { ...prev };
                                  delete nextUnits[key];
                                  return nextUnits;
                                }
                                if (!nextHasWork || key in prev) {
                                  if (!(key in prev)) return prev;
                                  const nextUnits = { ...prev };
                                  delete nextUnits[key];
                                  return nextUnits;
                                }
                                return prev;
                              });
                              if (!nextHasWork) {
                                setEditedModelBy((prev) => ({
                                  ...prev,
                                  [key]: "",
                                }));
                              } else if (!currModelBy) {
                                const fallback =
                                  splitNames(currDesigner)[0] || "";
                                if (fallback) {
                                  setEditedModelBy((prev) => ({
                                    ...prev,
                                    [key]: fallback,
                                  }));
                                }
                              }
                            }}
                          />
                        </div>
                        <OriginalValue
                          original={formatModelTypes(origModelTypes)}
                          current={formatModelTypes(currModelTypeRows)}
                          onRestore={() => {
                            revertField(setEditedModelType, key);
                            revertField(setEditedModelBy, key);
                            revertField(setEditedModelUnits, key);
                          }}
                        />
                      </td>

                      <td className={`${td}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setNotesModal({
                              caseId: row.case_id,
                              rowKey: key,
                              notes: jobCardNotes,
                              modelNotes: hasModel ? jobCardModelNotes : "",
                              category: currentCategory,
                              unitsNotes: "",
                              showUnitsNotes: false,
                            })
                          }
                          className={`rounded-lg px-2.5 py-1 text-[14px] font-medium ${
                            hasJobCardNotes
                              ? "bg-[#2fa9bf] text-white hover:bg-[#2296aa]"
                              : "border border-[#C9D4D6] bg-white text-[#4A4A48] hover:border-[#2fa9bf] hover:text-[#2fa9bf]"
                          }`}
                        >
                          Notes
                        </button>
                      </td>

                      <td className={`${td}`}>
                        <button
                          type="button"
                          disabled={loading || assigningKey === key}
                          onClick={() => handleAssign(row)}
                          className="rounded-lg bg-[#2fa9bf] px-2.5 py-1 text-[14px] font-medium text-white hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:bg-[#D8D8D2] disabled:text-[#8A8A85]"
                        >
                          {assigningKey === key ? "…" : "Assign"}
                        </button>
                      </td>
                    </tr>
                    {irItems.length ? (
                      <tr className="border-b border-[#E3E8E8]">
                        <td colSpan={colCount} className="px-2 pb-3 pt-0">
                          <InternalRedesignsUnitsPanel
                            items={irItems}
                            assignments={irAssignments}
                            parentDesigner={currDesigner}
                            parentQc2={currQc2}
                            designerOptions={designersList}
                            qc2Options={qc2List}
                            onAssignmentsChange={(next) =>
                              setEditedIrAssignments((prev) => ({
                                ...prev,
                                [key]: next,
                              }))
                            }
                          />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })}

                {!loading && activeCases.length === 0 ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="empty"
                    message="No cases found"
                  />
                ) : null}

                {loading ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="loading"
                    message="Loading…"
                  />
                ) : null}
              </tbody>
            </table>
          </div>
        </AdminTableCard>
      </AdminPageShell>

      {notesModal ? (
        <JobCardNotesModal
          notesModal={notesModal}
          onClose={() => setNotesModal(null)}
        />
      ) : null}

      {billingNoteModal ? (
        <UnitsBillingNoteModal
          notesModal={billingNoteModal}
          onClose={() => setBillingNoteModal(null)}
          onUnitsNotesChange={(value) => {
            const key = billingNoteModal.rowKey;
            setEditedUnitsNotes((prev) => ({ ...prev, [key]: value }));
            setBillingNoteModal((prev) =>
              prev ? { ...prev, unitsNotes: value } : prev,
            );
          }}
        />
      ) : null}
    </>
  );
};

export default CaseUnits;
